import classNames from "classnames";
import { Entry } from "contentful";

import Editorials from "~/types/editorials";
import Constants from "~/utils/constants";
import { assertEditorialType, isActiveEntry } from "~/utils/editorial-utils";

import styles from "./breakpoint-aware-asset.module.scss";
import MediaAsset from "./media-asset";

type Props = {
  entry: Entry<unknown>;
  className?: string;
  fill?: boolean;
  width?: number;
  height?: number;
  isHeroVideo?: boolean;
  videoConfig?: {
    muted?: boolean;
    loop?: boolean;
    autoPlay?: boolean;
    controls?: boolean;
    playsInline?: boolean;
  };
  noLazy?: boolean;
};

const defaultVideoConfig = Constants.DEFAULT_VIDEO_CONFIG;

export default function BreakpointAwareAsset({ entry, className, videoConfig, isHeroVideo, ...rest }: Props) {
  if (!entry || !isActiveEntry(entry)) return null;

  assertEditorialType<Editorials.ContentAwareAsset>(entry, "breakpointAwareAsset");

  const { posterMobile, posterDesktop, autoPlay, controls, loop, muted, playsInline } = entry.fields;
  let videoConfiguration;

  if (isHeroVideo) {
    videoConfiguration = {
      autoPlay: videoConfig?.autoPlay ?? autoPlay ?? defaultVideoConfig.autoPlay,
      controls: videoConfig?.controls ?? controls ?? defaultVideoConfig.controls,
      loop: videoConfig?.loop ?? loop ?? defaultVideoConfig.loop,
      muted: videoConfig?.muted ?? muted ?? defaultVideoConfig.muted,
      playsInline: videoConfig?.playsInline ?? playsInline ?? defaultVideoConfig.playsInline,
    };
  } else {
    videoConfiguration = {
      autoPlay: autoPlay ?? videoConfig?.autoPlay ?? defaultVideoConfig.autoPlay,
      controls: controls ?? videoConfig?.controls ?? defaultVideoConfig.controls,
      loop: loop ?? videoConfig?.loop ?? defaultVideoConfig.loop,
      muted: muted ?? videoConfig?.muted ?? defaultVideoConfig.muted,
      playsInline: playsInline ?? videoConfig?.playsInline ?? defaultVideoConfig.playsInline,
    };
  }

  return (
    <>
      <MediaAsset
        entry={entry.fields.mobileAsset}
        poster={posterMobile?.fields?.file?.url}
        className={classNames(styles.mobileAsset, className)}
        {...rest}
        sizes="(min-width: 900px) 0vw, 100vw"
        videoConfig={videoConfiguration}
      />
      <MediaAsset
        entry={entry.fields.desktopAsset}
        poster={posterDesktop?.fields?.file?.url}
        className={classNames(styles.desktopAsset, className)}
        {...rest}
        sizes="(min-width: 900px) 100vw, 0vw"
        videoConfig={videoConfiguration}
      />
    </>
  );
}
